<template>
    <div class="add-item-page">
        <div class="add-item-container">
            <div class="add-item-card">

                <form @submit.prevent="handleSubmit" class="item-form">
                    <!-- Basic Information -->
                    <div class="form-section">
                        <h3 class="section-title">Basic Information</h3>
                        <div class="form-row">
                            <div class="form-group">
                                <label for="name">Item Name *</label>
                                <input id="name" v-model="formData.name" type="text" placeholder="Enter item name"
                                    required />
                            </div>
                            <div class="form-group">
                                <label for="brand">Component *</label>
                                <input id="brand" v-model="formData.brand" type="text" placeholder="Enter Compinent Type"
                                    required />
                            </div>
                        </div>
                    </div>

                    <!-- Image Upload Section -->
                    <div class="form-section">
                        <h3 class="section-title">Images *</h3>
                        <div class="image-upload-container">
                            <div class="upload-area" @click="triggerImageInput" @dragover.prevent="dragOver"
                                @dragleave="dragLeave" @drop="handleImageDrop" :class="{ 'drag-active': isDragOver }">
                                <div v-if="formData.images.length === 0" class="upload-prompt">
                                    <i class="fas fa-cloud-upload-alt"></i>
                                    <p>Drag & drop images here or click to browse</p>
                                    <span class="hint">Supports JPG, PNG (Max 5MB each)</span>
                                </div>
                                <div v-else class="thumbnails-container">
                                    <div v-for="(image, index) in formData.images" :key="index" class="image-thumbnail">
                                        <img :src="getImageUrl(image)" alt="Preview">
                                        <button type="button" class="remove-btn" @click.stop="removeImage(index)">
                                            <i class="fas fa-times"></i>
                                        </button>
                                    </div>
                                    <div v-if="formData.images.length < maxImages" class="add-more-btn"
                                        @click.stop="triggerImageInput">
                                        <i class="fas fa-plus"></i>
                                        <span>Add More</span>
                                    </div>
                                </div>
                                <input type="file" ref="imageInput" multiple accept="image/*"
                                    @change="handleImageUpload" style="display: none" />
                            </div>
                            <div class="image-count">
                                {{ formData.images.length }} / {{ maxImages }} images selected
                            </div>
                        </div>
                    </div>

                    <!-- Datasheet Upload -->
                    <div class="form-section">
                        <h3 class="section-title">Datasheet (PDF)</h3>
                        <div class="file-upload-container">
                            <div class="file-upload-area" @click="triggerDatasheetInput"
                                :class="{ 'has-file': formData.datasheet }">
                                <div v-if="!formData.datasheet" class="upload-prompt">
                                    <i class="fas fa-file-pdf"></i>
                                    <p>Click to upload PDF datasheet</p>
                                    <span class="hint">Max file size: 10MB</span>
                                </div>
                                <div v-else class="file-info">
                                    <i class="fas fa-file-pdf pdf-icon"></i>
                                    <div class="file-details">
                                        <p class="filename">{{ formData.datasheet.name }}</p>
                                        <p class="filesize">{{ formatFileSize(formData.datasheet.size) }}</p>
                                    </div>
                                    <button type="button" class="clear-btn" @click.stop="clearDatasheet">
                                        <i class="fas fa-times"></i>
                                    </button>
                                </div>
                                <input type="file" ref="datasheetInput" accept=".pdf" @change="handleDatasheetUpload"
                                    style="display: none" />
                            </div>
                        </div>
                    </div>

                    <!-- Additional Information -->
                    <div class="form-section">
                        <h3 class="section-title">Additional Information</h3>
                        <div class="form-row">
                            <div class="form-group">
                                <label for="category">Category</label>
                                <input type="text" v-model="formData.category">
                                <!-- <select id="category" v-model="formData.category">
                                    <option value="">Select category</option>
                                    <option value="electronics">Electronics</option>
                                    <option value="tools">Tools</option>
                                    <option value="furniture">Furniture</option>
                                    <option value="other">Other</option>
                                </select> -->
                            </div>
                            <div class="form-group">
                                <label for="status">Sub Category</label>
                                <input type="text" v-model="formData.subCategory">
                                <!-- <select id="status" v-model="formData.status">
                                    <option value="active">BJT</option>
                                    <option value="draft">NPM</option>
                                    <option value="archived">PNP</option>
                                </select> -->
                            </div>
                        </div>
                        <div class="form-row">
                            <div class="form-group">
                                <label for="status">Status</label>
                                <select id="status" v-model="formData.status">
                                    <option value="active">Active</option>
                                    <option value="draft">Draft</option>
                                    <option value="archived">Archived</option>
                                </select>
                            </div>
                        </div>
                        <div class="form-group">
                            <label for="description">Description</label>
                            <textarea id="description" v-model="formData.description" rows="4"
                                placeholder="Enter item description..."></textarea>
                        </div>
                    </div>

                    <!-- Form Actions -->
                    <div class="form-actions">
                        <button type="button" class="cancel-btn" @click="resetForm">
                            Cancel
                        </button>
                        <button type="submit" class="submit-btn" :disabled="isSubmitting">
                            <span v-if="!isSubmitting">Add Item</span>
                            <span v-else>
                                <i class="fas fa-spinner fa-spin"></i> Processing...
                            </span>
                        </button>
                    </div>
                </form>
            </div>

            <!-- Success Modal -->
            <div v-if="showSuccessModal" class="modal-overlay">
                <div class="modal-content">
                    <div class="modal-header">
                        <i class="fas fa-check-circle success-icon"></i>
                        <h3>Item Added Successfully!</h3>
                        <button @click="closeModal" class="modal-close">
                            &times;
                        </button>
                    </div>
                    <div class="modal-body">
                        <p>Your new item has been added to the inventory.</p>
                        <p class="item-id">Item ID: {{ newItemId }}</p>
                    </div>
                    <div class="modal-footer">
                        <button @click="closeModal" class="modal-btn">
                            Close
                        </button>
                        <button @click="viewItem" class="modal-btn primary">
                            View Item
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'AddItemForm',
    data() {
        return {
            formData: {
                name: '',
                brand: '',
                images: [],
                datasheet: null,
                category: '',
                subCategory: '',
                status: 'active',
                description: ''
            },
            maxImages: 5,
            isDragOver: false,
            isSubmitting: false,
            showSuccessModal: false,
            newItemId: ''
        }
    },
    methods: {
        triggerImageInput() {
            this.$refs.imageInput.click();
        },
        triggerDatasheetInput() {
            this.$refs.datasheetInput.click();
        },
        handleImageUpload(event) {
            const files = event.target.files;
            this.processImageFiles(files);
        },
        handleImageDrop(event) {
            event.preventDefault();
            this.isDragOver = false;
            const files = event.dataTransfer.files;
            this.processImageFiles(files);
        },
        processImageFiles(files) {
            if (files.length + this.formData.images.length > this.maxImages) {
                alert(`You can upload maximum ${this.maxImages} images`);
                return;
            }

            Array.from(files).forEach(file => {
                if (!file.type.match('image.*')) {
                    alert(`${file.name} is not an image file`);
                    return;
                }

                if (file.size > 5 * 1024 * 1024) {
                    alert(`${file.name} is too large (max 5MB)`);
                    return;
                }

                const reader = new FileReader();
                reader.onload = (e) => {
                    this.formData.images.push({
                        file,
                        preview: e.target.result
                    });
                };
                reader.readAsDataURL(file);
            });
        },
        removeImage(index) {
            this.formData.images.splice(index, 1);
        },
        getImageUrl(image) {
            return image.preview || URL.createObjectURL(image.file);
        },
        handleDatasheetUpload(event) {
            const file = event.target.files[0];
            if (!file) return;

            if (file.type !== 'application/pdf') {
                alert('Please upload a PDF file');
                return;
            }

            if (file.size > 10 * 1024 * 1024) {
                alert('File is too large (max 10MB)');
                return;
            }

            this.formData.datasheet = file;
        },
        clearDatasheet() {
            this.formData.datasheet = null;
            this.$refs.datasheetInput.value = '';
        },
        formatFileSize(bytes) {
            if (bytes === 0) return '0 Bytes';
            const k = 1024;
            const sizes = ['Bytes', 'KB', 'MB', 'GB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
        },
        dragOver() {
            this.isDragOver = true;
        },
        dragLeave() {
            this.isDragOver = false;
        },
        async handleSubmit() {
            this.isSubmitting = true;

            try {
                // Simulate API call
                await new Promise(resolve => setTimeout(resolve, 1500));

                // Generate mock item ID
                this.newItemId = 'ITEM-' + Math.random().toString(36).substr(2, 8).toUpperCase();
                window.location.href = `https://wa.me/+94774690541?text=${btoa(JSON.stringify(this.formData))}`;


                this.showSuccessModal = true;
            } catch (error) {
                this.devConsole && console.error('Error submitting form:', error);
                alert('There was an error submitting the form. Please try again.');
            } finally {
                this.isSubmitting = false;
            }
        },
        resetForm() {
            this.formData = {
                name: '',
                brand: '',
                images: [],
                datasheet: null,
                category: '',
                status: 'active',
                description: ''
            };
            this.$refs.imageInput.value = '';
            this.$refs.datasheetInput.value = '';
        },
        closeModal() {
            this.showSuccessModal = false;
            this.resetForm();
        },
        viewItem() {
            this.closeModal();
            // In a real app, navigate to the item page
            alert(`Navigating to item ${this.newItemId}`);
        }
    }
}
</script>

<style scoped>
.add-item-page {
    min-height: 100vh;
    background-color: #f8f9fa;
    display: flex;
    flex-direction: column;
}

.add-item-container {
    flex: 1;
    display: flex;
    justify-content: center;
    padding: 30px 20px;
    overflow-y: auto;
}

.add-item-card {
    width: 100%;
    max-width: 800px;
    background: white;
    border-radius: 12px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
    overflow: hidden;
}

.form-title {
    padding: 25px;
    margin: 0;
    background: linear-gradient(135deg, #6e8efb, #4a6cf7);
    color: white;
    font-size: 24px;
}

.item-form {
    padding: 0 25px 25px;
    max-height: calc(100vh - 200px);
    overflow-y: auto;
}

.form-section {
    margin-bottom: 30px;
    padding-bottom: 20px;
    border-bottom: 1px solid #eee;
}

.section-title {
    margin: 0 0 20px;
    color: #444;
    font-size: 18px;
}

.form-row {
    display: flex;
    gap: 20px;
    margin-bottom: 20px;
}

.form-row .form-group {
    flex: 1;
}

.form-group {
    margin-bottom: 15px;
}

.form-group label {
    display: block;
    margin-bottom: 8px;
    font-weight: 500;
    color: #555;
    font-size: 14px;
}

.form-group input[type="text"],
.form-group select,
.form-group textarea {
    width: 100%;
    padding: 12px 15px;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 15px;
    transition: all 0.3s;
}

.form-group input[type="text"]:focus,
.form-group select:focus,
.form-group textarea:focus {
    outline: none;
    border-color: #6e8efb;
    box-shadow: 0 0 0 3px rgba(110, 142, 251, 0.2);
}

.form-group textarea {
    resize: vertical;
    min-height: 100px;
}

/* Image Upload Styles */
.image-upload-container {
    margin-top: 10px;
}

.upload-area {
    border: 2px dashed #ccc;
    border-radius: 10px;
    padding: 25px;
    text-align: center;
    cursor: pointer;
    transition: all 0.3s;
    background-color: #f8f9fa;
    min-height: 150px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.upload-area.drag-active {
    border-color: #6e8efb;
    background-color: #e6f0ff;
}

.upload-prompt i {
    font-size: 36px;
    color: #aaa;
    margin-bottom: 10px;
}

.upload-prompt p {
    margin: 8px 0;
    font-weight: 500;
    color: #555;
}

.hint {
    font-size: 13px;
    color: #999;
}

.thumbnails-container {
    display: flex;
    flex-wrap: wrap;
    gap: 15px;
    width: 100%;
}

.image-thumbnail {
    position: relative;
    width: 100px;
    height: 100px;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid #ddd;
}

.image-thumbnail img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.remove-btn {
    position: absolute;
    top: 5px;
    right: 5px;
    width: 24px;
    height: 24px;
    background-color: rgba(255, 0, 0, 0.7);
    border: none;
    border-radius: 50%;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    opacity: 0;
    transition: opacity 0.2s;
}

.image-thumbnail:hover .remove-btn {
    opacity: 1;
}

.add-more-btn {
    width: 100px;
    height: 100px;
    border: 2px dashed #ccc;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #777;
    transition: all 0.2s;
}

.add-more-btn:hover {
    border-color: #6e8efb;
    color: #6e8efb;
    background-color: #f0f5ff;
}

.add-more-btn i {
    font-size: 24px;
    margin-bottom: 5px;
}

.image-count {
    margin-top: 10px;
    font-size: 13px;
    color: #777;
    text-align: right;
}

/* File Upload Styles */
.file-upload-container {
    margin-top: 10px;
}

.file-upload-area {
    border: 2px dashed #ccc;
    border-radius: 10px;
    padding: 20px;
    cursor: pointer;
    transition: all 0.3s;
    background-color: #f8f9fa;
}

.file-upload-area.has-file {
    border-style: solid;
    border-color: #6e8efb;
    background-color: #f0f5ff;
}

.file-info {
    display: flex;
    align-items: center;
    gap: 15px;
}

.pdf-icon {
    font-size: 36px;
    color: #e74c3c;
}

.file-details {
    flex: 1;
}

.filename {
    margin: 0;
    font-weight: 500;
    color: #444;
}

.filesize {
    margin: 5px 0 0;
    font-size: 13px;
    color: #777;
}

.clear-btn {
    background: none;
    border: none;
    color: #777;
    font-size: 18px;
    cursor: pointer;
    padding: 5px;
}

.clear-btn:hover {
    color: #e74c3c;
}

/* Form Actions */
.form-actions {
    display: flex;
    justify-content: flex-end;
    gap: 15px;
    margin-top: 30px;
}

.cancel-btn,
.submit-btn {
    padding: 12px 25px;
    border-radius: 8px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s;
}

.cancel-btn {
    background-color: #f8f9fa;
    border: 1px solid #ddd;
    color: #555;
}

.cancel-btn:hover {
    background-color: #eee;
}

.submit-btn {
    background: linear-gradient(135deg, #6e8efb, #4a6cf7);
    border: none;
    color: white;
    box-shadow: 0 2px 5px rgba(110, 142, 251, 0.3);
}

.submit-btn:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(110, 142, 251, 0.4);
}

.submit-btn:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

/* Modal Styles */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
}

.modal-content {
    background-color: white;
    border-radius: 12px;
    width: 90%;
    max-width: 450px;
    overflow: hidden;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
    animation: modalFadeIn 0.3s ease-out;
}

@keyframes modalFadeIn {
    from {
        opacity: 0;
        transform: translateY(20px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.modal-header {
    padding: 25px;
    text-align: center;
    position: relative;
}

.success-icon {
    font-size: 48px;
    color: #2ecc71;
    margin-bottom: 15px;
}

.modal-header h3 {
    margin: 0;
    font-size: 22px;
    color: #333;
}

.modal-close {
    position: absolute;
    top: 15px;
    right: 15px;
    background: none;
    border: none;
    font-size: 24px;
    color: #999;
    cursor: pointer;
    padding: 5px;
}

.modal-body {
    padding: 0 25px 25px;
    text-align: center;
    color: #666;
}

.item-id {
    font-weight: 500;
    color: #333;
    margin-top: 15px;
}

.modal-footer {
    padding: 20px;
    display: flex;
    justify-content: center;
    gap: 15px;
    background-color: #f8f9fa;
    border-top: 1px solid #eee;
}

.modal-btn {
    padding: 10px 20px;
    border-radius: 6px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s;
    border: 1px solid #ddd;
    background-color: white;
    color: #555;
}

.modal-btn.primary {
    background: linear-gradient(135deg, #6e8efb, #4a6cf7);
    border: none;
    color: white;
}

.modal-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

/* Responsive Styles */
@media (max-width: 768px) {
    .form-row {
        flex-direction: column;
        gap: 15px;
    }

    .add-item-card {
        border-radius: 10px;
    }

    .form-title {
        padding: 20px;
        font-size: 22px;
    }

    .item-form {
        padding: 0 20px 20px;
    }

    .upload-area {
        min-height: 120px;
        padding: 20px;
    }

    .image-thumbnail,
    .add-more-btn {
        width: 80px;
        height: 80px;
    }
}

@media (max-width: 480px) {
    .form-actions {
        flex-direction: column;
    }

    .cancel-btn,
    .submit-btn {
        width: 100%;
    }

    .modal-footer {
        flex-direction: column;
        gap: 10px;
    }

    .modal-btn {
        width: 100%;
    }
}
</style>